import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import UniversityList from "../components/UniversityList";
import api, { API_ENDPOINTS } from "../config/api";
import { transformUniversityData } from "../utils/transformUniversityData";
import BackToTop from "../components/BackToTop";
import SEO from "../components/SEO";
import "../styles/App.css";
import { useAuth } from "../context/AuthContext";

// Import images
import heroBackground from "../assets/images/hero-background.jpg";
import usaCountry from "../assets/images/usa-country.jpg";
import canadaCountry from "../assets/images/canada-country.jpg";
import ukCountry from "../assets/images/uk-country.jpg";
import australiaCountry from "../assets/images/australia-country.jpg";

// Helper function to convert tuition fee range to min/max values
const getTuitionFeeRange = (range) => {
  switch (range) {
    case "25000":
      return { min: 0, max: 25000 };
    case "35000":
      return { min: 25001, max: 35000 };
    case "45000":
      return { min: 35001, max: 45000 };
    case "55000":
      return { min: 45001, max: 55000 };
    case "65000":
      return { min: 55001, max: 65000 };
    case "75000":
      return { min: 65001, max: 75000 };
    case "85000":
      return { min: 75001, max: 85000 };
    case "85000+":
      return { min: 85001, max: undefined };
    default:
      return { min: undefined, max: undefined };
  }
};

const HomePage = () => {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("All");
  const [tuitionFeeRange, setTuitionFeeRange] = useState("All");
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [allUniversities, setAllUniversities] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [itemsPerPage] = useState(6);
  const [universitySuggestions, setUniversitySuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [justCompletedSearch, setJustCompletedSearch] = useState(false);
  const [justSelectedSuggestion, setJustSelectedSuggestion] = useState(false);
  const [suggestionSelectionTime, setSuggestionSelectionTime] = useState(0);
  const [suppressSuggestions, setSuppressSuggestions] = useState(false);
  const suggestionRef = useRef(null);

  // Handle university suggestion selection
  const handleSuggestionSelect = useCallback((suggestion) => {
    setSearchTerm(suggestion.name);
    setUniversitySuggestions([]);
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
    setSuggestionsLoading(false);
    setJustSelectedSuggestion(true);
    setSuggestionSelectionTime(Date.now());
    suggestionRef.current = {
      selected: true,
      timestamp: Date.now(),
      searchTerm: suggestion.name,
    };
    setIsSearching(true);
    setSuppressSuggestions(true);
    setTimeout(() => {
      setJustSelectedSuggestion(false);
      setSuggestionSelectionTime(0);
      suggestionRef.current = null;
    }, 10000);
  }, []);

  useEffect(() => {
    return () => {
      suggestionRef.current = null;
    };
  }, []);

  // Effect for keyboard navigation in suggestions
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showSuggestions || universitySuggestions.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedSuggestionIndex((prev) =>
            prev < universitySuggestions.length - 1 ? prev + 1 : 0
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedSuggestionIndex((prev) =>
            prev > 0 ? prev - 1 : universitySuggestions.length - 1
          );
          break;
        case "Enter":
          e.preventDefault();
          if (selectedSuggestionIndex >= 0) {
            handleSuggestionSelect(
              universitySuggestions[selectedSuggestionIndex]
            );
          } else {
            setIsSearching(true);
          }
          break;
        case "Escape":
          setShowSuggestions(false);
          setSelectedSuggestionIndex(-1);
          break;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    showSuggestions,
    universitySuggestions,
    selectedSuggestionIndex,
    handleSuggestionSelect,
  ]);

  // Add fetchUniversitySuggestions useCallback
  const fetchUniversitySuggestions = useCallback(
    async (query) => {
      try {
        if (!query || query.length < 2) {
          setUniversitySuggestions([]);
          return;
        }
        setSuggestionsLoading(true);
        const endpoint = isLoggedIn
          ? API_ENDPOINTS.UNIVERSITIES.SEARCH
          : API_ENDPOINTS.PUBLIC_UNIVERSITIES.SEARCH;
        const params = new URLSearchParams({
          query: query.trim(),
          limit: "6",
        });
        if (selectedCountry !== "All") {
          params.append("country", selectedCountry);
        }
        if (isLoggedIn && tuitionFeeRange !== "All") {
          const tuitionRange = getTuitionFeeRange(tuitionFeeRange);
          if (tuitionRange.min !== undefined) {
            params.append("minTuition", tuitionRange.min.toString());
          }
          if (tuitionRange.max !== undefined) {
            params.append("maxTuition", tuitionRange.max.toString());
          }
        }
        const url = `${endpoint}?${params.toString()}`;
        const response = await api.get(url);
        setUniversitySuggestions(response.data.data || []);
      } catch (error) {
        setUniversitySuggestions([]);
      } finally {
        setSuggestionsLoading(false);
      }
    },
    [selectedCountry, tuitionFeeRange, isLoggedIn]
  );

  // Update university suggestions when search term changes (debounced, edge-case aware)
  useEffect(() => {
    if (suppressSuggestions) return;
    if (isSearching) return;
    if (justCompletedSearch) {
      setJustCompletedSearch(false);
      return;
    }
    if (
      justSelectedSuggestion ||
      (suggestionSelectionTime > 0 &&
        Date.now() - suggestionSelectionTime < 10000) ||
      (suggestionRef.current && suggestionRef.current.selected)
    ) {
      return;
    }
    if (searchTerm.length < 2) {
      setUniversitySuggestions([]);
      setShowSuggestions(false);
      return;
    }
    if (
      searchTerm.length >= 2 &&
      !justSelectedSuggestion &&
      !isSearching &&
      suggestionSelectionTime === 0 &&
      (!suggestionRef.current || !suggestionRef.current.selected)
    ) {
      const timer = setTimeout(() => {
        fetchUniversitySuggestions(searchTerm);
        setShowSuggestions(true);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [
    searchTerm,
    fetchUniversitySuggestions,
    isSearching,
    justCompletedSearch,
    justSelectedSuggestion,
    suggestionSelectionTime,
    suppressSuggestions,
  ]);

  // Effect: Handle search functionality (separate from suggestions)
  useEffect(() => {
    // Only trigger search if user is actively searching (pressed Enter) and searchTerm is not empty
    if (isSearching && searchTerm.trim() && searchTerm.length >= 2) {
      const timer = setTimeout(() => {
        // This will trigger a search of the main list
        const performSearch = async () => {
          try {
            setSearchLoading(true); // Show subtle loading indicator
            setCurrentPage(1); // Reset to first page

            const endpoint = isLoggedIn
              ? API_ENDPOINTS.UNIVERSITIES.LIST
              : API_ENDPOINTS.PUBLIC_UNIVERSITIES.LIST;

            // Build URL with pagination parameters
            const params = new URLSearchParams({
              page: "1", // Always start with page 1
              limit: itemsPerPage.toString(),
              search: searchTerm.trim(), // Add search parameter
            });

            // Add country filter if selected
            if (selectedCountry !== "All") {
              params.append("country", selectedCountry);
            }

            // Add tuition fee filter if selected and user is authenticated
            if (isLoggedIn && tuitionFeeRange !== "All") {
              const tuitionRange = getTuitionFeeRange(tuitionFeeRange);
              if (tuitionRange.min !== undefined) {
                params.append("minTuition", tuitionRange.min.toString());
              }
              if (tuitionRange.max !== undefined) {
                params.append("maxTuition", tuitionRange.max.toString());
              }
            }

            const url = `${endpoint}?${params.toString()}`;
            const response = await api.get(url);
            const data = response.data;

            let fetchedUniversities = [];
            if (isLoggedIn) {
              if (data.data && Array.isArray(data.data)) {
                fetchedUniversities = data.data;
              } else if (data.usaUniversities && data.canadaUniversities) {
                fetchedUniversities = [
                  ...data.usaUniversities.map((uni) =>
                    transformUniversityData(uni, "USA")
                  ),
                  ...data.canadaUniversities.map((uni) =>
                    transformUniversityData(uni, "Canada")
                  ),
                ];
              } else {
                fetchedUniversities = Array.isArray(data) ? data : [];
              }
            } else {
              if (data.data && Array.isArray(data.data)) {
                fetchedUniversities = data.data;
              } else {
                fetchedUniversities = data.data || data;
              }
            }

            if (data.pagination) {
              setHasMore(data.pagination.hasNextPage);
            } else {
              setHasMore(fetchedUniversities.length >= itemsPerPage);
            }

            const transformedUniversities = fetchedUniversities.map((uni) => {
              try {
                return {
                  name:
                    uni["University Name"] || uni.name || "Unknown University",
                  city:
                    uni["City, State"]?.split(",")[0]?.trim() ||
                    uni.city ||
                    uni.location?.split(",")[0]?.trim(),
                  state:
                    uni["City, State"]?.split(",")[1]?.trim() ||
                    uni.state ||
                    uni.location?.split(",")[1]?.trim(),
                  country: uni.country || "Unknown",
                  tuitionFees:
                    uni["Tuition Fees"] ||
                    uni["Tution Fees"] ||
                    uni.tuitionFees,
                  livingExpenses: uni["Living Expenses"] || uni.livingExpenses,
                  acceptanceRate: uni["Acceptance rate"] || uni.acceptanceRate,
                  averageGRE: uni["Average GRE score"] || uni.averageGRE,
                  averageToefl: uni["Average Toefl Score"] || uni.averageToefl,
                  averageGpa: uni["Average Gpa"] || uni.averageGPA,
                  summary: uni["Summary/About"] || uni.summary,
                  "University Name": uni["University Name"] || uni.name,
                  "Summary/About": uni["Summary/About"] || uni.summary,
                  Country: uni.country || "Unknown",
                  "City, State":
                    uni["City, State"] ||
                    uni.location ||
                    `${uni.city || ""}, ${uni.state || ""}`.trim(),
                  "Total Enrolled": uni["Total Enrolled"] || uni.totalEnrolled,
                  "International Students":
                    uni["International Students"] || uni.internationalStudents,
                  "Male percentage":
                    uni["Male percentage"] || uni.malePercentage,
                  "Female Percentage":
                    uni["Female Percentage"] || uni.femalePercentage,
                  "Average GRE score":
                    uni["Average GRE score"] || uni.averageGRE,
                  "Verbal Reasoning": uni["Verbal Reasoning"] || uni.greVerbal,
                  "Quantitative Reasoning":
                    uni["Quantitative Reasoning"] || uni.greQuantitative,
                  "Average Toefl Score":
                    uni["Average Toefl Score"] || uni.averageToefl,
                  "Minimum Toefl Score Required":
                    uni["Minimum Toefl Score Required"] || uni.minToefl,
                  "Average Ielts Score":
                    uni["Average Ielts Score"] || uni.averageIelts,
                  "Minimum Ielts Score Required":
                    uni["Minimum Ielts Score Required"] || uni.minIelts,
                  "Average Gpa": uni["Average Gpa"] || uni.averageGpa,
                  "Acceptance rate":
                    uni["Acceptance rate"] || uni.acceptanceRate,
                  "Tution Fees":
                    uni["Tution Fees"] ||
                    uni["Tuition Fees"] ||
                    uni.tuitionFees,
                  "Tuition Fees":
                    uni["Tuition Fees"] ||
                    uni["Tution Fees"] ||
                    uni.tuitionFees,
                  "Living Expenses":
                    uni["Living Expenses"] || uni.livingExpenses,
                  "Jugaad Living Expenses (USD) for one year":
                    uni["Jugaad Living Expenses (USD) for one year"] ||
                    uni.jugaadLivingExpenses,
                  "Application Fees for Masters":
                    uni["Application Fees for Masters"] ||
                    uni.applicationFeeMasters,
                  "Application Fee for Undergrad":
                    uni["Application Fee for Undergrad"] ||
                    uni.applicationFeeUndergrad,
                  "Financial Aid Link":
                    uni["Financial Aid Link"] || uni.financialAidLink,
                  "Average Salary": uni["Average Salary"] || uni.averageSalary,
                  Employed: uni["Employed"] || uni.employmentRate,
                  "US News National rank":
                    uni["US News National rank"] || uni.usNewsRank,
                  "QS World University Rankings":
                    uni["QS World University Rankings"] || uni.qsWorldRank,
                  "Times Higher Education Rank":
                    uni["Times Higher Education Rank"] || uni.timesHigherRank,
                  Pros: uni["Pros"] || uni.pros,
                  Cons: uni["Cons"] || uni.cons,
                  slug: uni.slug,
                  ...uni,
                };
              } catch (transformError) {
                return {
                  name: uni.name || "Unknown University",
                  city: uni.city || "",
                  state: uni.state || "",
                  country: uni.country || "Unknown",
                  slug: uni.slug,
                  ...uni,
                };
              }
            });

            setAllUniversities(transformedUniversities);
            setUniversities(transformedUniversities);
            setError(null);
            setIsSearching(false); // Reset searching state after search is complete
            setJustCompletedSearch(true); // Mark that a search was just completed
          } catch (err) {
            setError("Failed to search universities. Please try again later.");
            setIsSearching(false); // Reset searching state on error
            setJustCompletedSearch(true); // Mark that a search was just completed (even on error)
          } finally {
            setSearchLoading(false); // Hide loading indicator
          }
        };
        performSearch();
      }, 100); // Reduced delay for better responsiveness
      return () => clearTimeout(timer);
    } else if (searchTerm.trim() === "" && isSearching) {
      // If search term is cleared and user was searching, reload the original list
      // (You may want to call your initial data fetch here)
    }
  }, [
    isSearching,
    searchTerm,
    selectedCountry,
    itemsPerPage,
    tuitionFeeRange,
    isLoggedIn,
  ]);

  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);
      const nextPage = currentPage + 1;

      const endpoint = isLoggedIn
        ? API_ENDPOINTS.UNIVERSITIES.LIST
        : API_ENDPOINTS.PUBLIC_UNIVERSITIES.LIST;

      // Build URL with pagination parameters
      const params = new URLSearchParams({
        page: nextPage.toString(),
        limit: itemsPerPage.toString(),
      });

      // Add search parameter if provided
      if (searchTerm.trim()) {
        params.append("search", searchTerm.trim());
      }

      // Add country filter if selected
      if (selectedCountry !== "All") {
        params.append("country", selectedCountry);
      }

      // Add tuition fee filter if selected and user is authenticated
      if (isLoggedIn && tuitionFeeRange !== "All") {
        const tuitionRange = getTuitionFeeRange(tuitionFeeRange);
        if (tuitionRange.min !== undefined) {
          params.append("minTuition", tuitionRange.min.toString());
        }
        if (tuitionRange.max !== undefined) {
          params.append("maxTuition", tuitionRange.max.toString());
        }
      }

      const url = `${endpoint}?${params.toString()}`;
      const response = await api.get(url);
      const data = response.data;

      let fetchedUniversities = [];
      if (isLoggedIn) {
        if (data.data && Array.isArray(data.data)) {
          fetchedUniversities = data.data;
        } else if (data.usaUniversities && data.canadaUniversities) {
          fetchedUniversities = [
            ...data.usaUniversities.map((uni) =>
              transformUniversityData(uni, "USA")
            ),
            ...data.canadaUniversities.map((uni) =>
              transformUniversityData(uni, "Canada")
            ),
          ];
        } else {
          fetchedUniversities = Array.isArray(data) ? data : [];
        }
      } else {
        if (data.data && Array.isArray(data.data)) {
          fetchedUniversities = data.data;
        } else {
          fetchedUniversities = data.data || data;
        }
      }

      // Update hasMore based on pagination info
      if (data.pagination) {
        setHasMore(data.pagination.hasNextPage);
      } else {
        setHasMore(fetchedUniversities.length >= itemsPerPage);
      }

      // Transform the data to ensure consistent structure (copy from App.js)
      const transformedUniversities = fetchedUniversities.map((uni) => {
        try {
          return {
            name: uni["University Name"] || uni.name || "Unknown University",
            city:
              uni["City, State"]?.split(",")[0]?.trim() ||
              uni.city ||
              uni.location?.split(",")[0]?.trim(),
            state:
              uni["City, State"]?.split(",")[1]?.trim() ||
              uni.state ||
              uni.location?.split(",")[1]?.trim(),
            country: uni.country || "Unknown",
            tuitionFees:
              uni["Tuition Fees"] || uni["Tution Fees"] || uni.tuitionFees,
            livingExpenses: uni["Living Expenses"] || uni.livingExpenses,
            acceptanceRate: uni["Acceptance rate"] || uni.acceptanceRate,
            averageGRE: uni["Average GRE score"] || uni.averageGRE,
            averageToefl: uni["Average Toefl Score"] || uni.averageToefl,
            averageGpa: uni["Average Gpa"] || uni.averageGPA,
            summary: uni["Summary/About"] || uni.summary,
            // Add all the fields needed for university detail page
            "University Name": uni["University Name"] || uni.name,
            "Summary/About": uni["Summary/About"] || uni.summary,
            Country: uni.country || "Unknown",
            "City, State":
              uni["City, State"] ||
              uni.location ||
              `${uni.city || ""}, ${uni.state || ""}`.trim(),
            "Total Enrolled": uni["Total Enrolled"] || uni.totalEnrolled,
            "International Students":
              uni["International Students"] || uni.internationalStudents,
            "Male percentage": uni["Male percentage"] || uni.malePercentage,
            "Female Percentage":
              uni["Female Percentage"] || uni.femalePercentage,
            "Average GRE score": uni["Average GRE score"] || uni.averageGRE,
            "Verbal Reasoning": uni["Verbal Reasoning"] || uni.greVerbal,
            "Quantitative Reasoning":
              uni["Quantitative Reasoning"] || uni.greQuantitative,
            "Average Toefl Score":
              uni["Average Toefl Score"] || uni.averageToefl,
            "Minimum Toefl Score Required":
              uni["Minimum Toefl Score Required"] || uni.minToefl,
            "Average Ielts Score":
              uni["Average Ielts Score"] || uni.averageIelts,
            "Minimum Ielts Score Required":
              uni["Minimum Ielts Score Required"] || uni.minIelts,
            "Average Gpa": uni["Average Gpa"] || uni.averageGpa,
            "Acceptance rate": uni["Acceptance rate"] || uni.acceptanceRate,
            "Tution Fees":
              uni["Tution Fees"] || uni["Tuition Fees"] || uni.tuitionFees,
            "Tuition Fees":
              uni["Tuition Fees"] || uni["Tution Fees"] || uni.tuitionFees,
            "Living Expenses": uni["Living Expenses"] || uni.livingExpenses,
            "Jugaad Living Expenses (USD) for one year":
              uni["Jugaad Living Expenses (USD) for one year"] ||
              uni.jugaadLivingExpenses,
            "Application Fees for Masters":
              uni["Application Fees for Masters"] || uni.applicationFeeMasters,
            "Application Fee for Undergrad":
              uni["Application Fee for Undergrad"] ||
              uni.applicationFeeUndergrad,
            "Financial Aid Link":
              uni["Financial Aid Link"] || uni.financialAidLink,
            "Average Salary": uni["Average Salary"] || uni.averageSalary,
            Employed: uni["Employed"] || uni.employmentRate,
            "US News National rank":
              uni["US News National rank"] || uni.usNewsRank,
            "QS World University Rankings":
              uni["QS World University Rankings"] || uni.qsWorldRank,
            "Times Higher Education Rank":
              uni["Times Higher Education Rank"] || uni.timesHigherRank,
            Pros: uni["Pros"] || uni.pros,
            Cons: uni["Cons"] || uni.cons,
            slug: uni.slug,
            ...uni,
          };
        } catch (transformError) {
          return {
            name: uni.name || "Unknown University",
            city: uni.city || "",
            state: uni.state || "",
            country: uni.country || "Unknown",
            slug: uni.slug,
            ...uni,
          };
        }
      });

      // Append new universities to existing ones instead of replacing
      setAllUniversities((prevUniversities) => [
        ...prevUniversities,
        ...transformedUniversities,
      ]);
      setUniversities((prevUniversities) => [
        ...prevUniversities,
        ...transformedUniversities,
      ]);
      setCurrentPage(nextPage);

      // Update hasMore based on pagination info
      if (data.pagination) {
        setHasMore(data.pagination.hasNextPage);
      } else {
        // Fallback: check if we got fewer items than requested
        setHasMore(transformedUniversities.length >= itemsPerPage);
      }
    } catch (err) {
      setError("Failed to load more universities. Please try again.");
    } finally {
      setLoadingMore(false);
    }
  };

  // Handle search form submission (prevent default form behavior)
  const handleSearchSubmit = (e) => {
    e.preventDefault(); // Prevent form submission and page refresh
    setIsSearching(true); // Mark as actively searching
    // The search will be handled by the useEffect that watches searchTerm
  };

  // Handle search change - reset to page 1
  const handleSearchChange = (e) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    setIsSearching(false);
    setSelectedSuggestionIndex(-1);
    setSuppressSuggestions(false);
    if (!justSelectedSuggestion) {
      setJustCompletedSearch(false);
    } else {
      setJustSelectedSuggestion(false);
    }
  };

  // Handle search input focus
  const handleSearchFocus = () => {
    if (searchTerm.length >= 2 && universitySuggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  // Handle search input blur
  const handleSearchBlur = () => {
    // Delay hiding suggestions to allow for clicks
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  // Handle country change - reset to page 1
  const handleCountryChange = async (e) => {
    const newCountry = e.target.value;
    setSelectedCountry(newCountry);
  };

  // Handle tuition fee change - reset to page 1
  const handleTuitionFeeChange = async (e) => {
    const newTuitionRange = e.target.value;
    setTuitionFeeRange(newTuitionRange);
  };

  // Add initial data fetch useEffect
  useEffect(() => {
    if (searchTerm.trim() !== "") return; // Don't run if searching
    setLoading(true);
    const fetchInitialData = async () => {
      try {
        setCurrentPage(1);
        const endpoint = isLoggedIn
          ? API_ENDPOINTS.UNIVERSITIES.LIST
          : API_ENDPOINTS.PUBLIC_UNIVERSITIES.LIST;
        const params = new URLSearchParams({
          page: "1",
          limit: itemsPerPage.toString(),
        });
        if (selectedCountry !== "All") {
          params.append("country", selectedCountry);
        }
        if (isLoggedIn && tuitionFeeRange !== "All") {
          const tuitionRange = getTuitionFeeRange(tuitionFeeRange);
          if (tuitionRange.min !== undefined) {
            params.append("minTuition", tuitionRange.min.toString());
          }
          if (tuitionRange.max !== undefined) {
            params.append("maxTuition", tuitionRange.max.toString());
          }
        }
        const url = `${endpoint}?${params.toString()}`;
        const response = await api.get(url);
        const data = response.data;
        let fetchedUniversities = [];
        if (isLoggedIn) {
          if (data.data && Array.isArray(data.data)) {
            fetchedUniversities = data.data;
          } else if (data.usaUniversities && data.canadaUniversities) {
            fetchedUniversities = [
              ...data.usaUniversities.map((uni) =>
                transformUniversityData(uni, "USA")
              ),
              ...data.canadaUniversities.map((uni) =>
                transformUniversityData(uni, "Canada")
              ),
            ];
          } else {
            fetchedUniversities = Array.isArray(data) ? data : [];
          }
        } else {
          if (data.data && Array.isArray(data.data)) {
            fetchedUniversities = data.data;
          } else {
            fetchedUniversities = data.data || data;
          }
        }
        if (data.pagination) {
          setHasMore(data.pagination.hasNextPage);
        } else {
          setHasMore(fetchedUniversities.length >= itemsPerPage);
        }
        const transformedUniversities = fetchedUniversities.map((uni) => {
          try {
            return {
              name: uni["University Name"] || uni.name || "Unknown University",
              city:
                uni["City, State"]?.split(",")[0]?.trim() ||
                uni.city ||
                uni.location?.split(",")[0]?.trim(),
              state:
                uni["City, State"]?.split(",")[1]?.trim() ||
                uni.state ||
                uni.location?.split(",")[1]?.trim(),
              country: uni.country || "Unknown",
              tuitionFees:
                uni["Tuition Fees"] || uni["Tution Fees"] || uni.tuitionFees,
              livingExpenses: uni["Living Expenses"] || uni.livingExpenses,
              acceptanceRate: uni["Acceptance rate"] || uni.acceptanceRate,
              averageGRE: uni["Average GRE score"] || uni.averageGRE,
              averageToefl: uni["Average Toefl Score"] || uni.averageToefl,
              averageGpa: uni["Average Gpa"] || uni.averageGPA,
              summary: uni["Summary/About"] || uni.summary,
              "University Name": uni["University Name"] || uni.name,
              "Summary/About": uni["Summary/About"] || uni.summary,
              Country: uni.country || "Unknown",
              "City, State":
                uni["City, State"] ||
                uni.location ||
                `${uni.city || ""}, ${uni.state || ""}`.trim(),
              "Total Enrolled": uni["Total Enrolled"] || uni.totalEnrolled,
              "International Students":
                uni["International Students"] || uni.internationalStudents,
              "Male percentage": uni["Male percentage"] || uni.malePercentage,
              "Female Percentage":
                uni["Female Percentage"] || uni.femalePercentage,
              "Average GRE score": uni["Average GRE score"] || uni.averageGRE,
              "Verbal Reasoning": uni["Verbal Reasoning"] || uni.greVerbal,
              "Quantitative Reasoning":
                uni["Quantitative Reasoning"] || uni.greQuantitative,
              "Average Toefl Score":
                uni["Average Toefl Score"] || uni.averageToefl,
              "Minimum Toefl Score Required":
                uni["Minimum Toefl Score Required"] || uni.minToefl,
              "Average Ielts Score":
                uni["Average Ielts Score"] || uni.averageIelts,
              "Minimum Ielts Score Required":
                uni["Minimum Ielts Score Required"] || uni.minIelts,
              "Average Gpa": uni["Average Gpa"] || uni.averageGpa,
              "Acceptance rate": uni["Acceptance rate"] || uni.acceptanceRate,
              "Tution Fees":
                uni["Tution Fees"] || uni["Tuition Fees"] || uni.tuitionFees,
              "Tuition Fees":
                uni["Tuition Fees"] || uni["Tution Fees"] || uni.tuitionFees,
              "Living Expenses": uni["Living Expenses"] || uni.livingExpenses,
              "Jugaad Living Expenses (USD) for one year":
                uni["Jugaad Living Expenses (USD) for one year"] ||
                uni.jugaadLivingExpenses,
              "Application Fees for Masters":
                uni["Application Fees for Masters"] ||
                uni.applicationFeeMasters,
              "Application Fee for Undergrad":
                uni["Application Fee for Undergrad"] ||
                uni.applicationFeeUndergrad,
              "Financial Aid Link":
                uni["Financial Aid Link"] || uni.financialAidLink,
              "Average Salary": uni["Average Salary"] || uni.averageSalary,
              Employed: uni["Employed"] || uni.employmentRate,
              "US News National rank":
                uni["US News National rank"] || uni.usNewsRank,
              "QS World University Rankings":
                uni["QS World University Rankings"] || uni.qsWorldRank,
              "Times Higher Education Rank":
                uni["Times Higher Education Rank"] || uni.timesHigherRank,
              Pros: uni["Pros"] || uni.pros,
              Cons: uni["Cons"] || uni.cons,
              slug: uni.slug,
              ...uni,
            };
          } catch (transformError) {
            return {
              name: uni.name || "Unknown University",
              city: uni.city || "",
              state: uni.state || "",
              country: uni.country || "Unknown",
              slug: uni.slug,
              ...uni,
            };
          }
        });
        setAllUniversities(transformedUniversities);
        setUniversities(transformedUniversities);
        setError(null);
      } catch (err) {
        setError("Failed to fetch university data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
    // Only run on mount and when these change
  }, [isLoggedIn, selectedCountry, tuitionFeeRange, itemsPerPage, searchTerm]);

  useEffect(() => {
    const onSubmit = (e) => {
      e.preventDefault();
    };
    window.addEventListener("submit", onSubmit, true);
    return () => window.removeEventListener("submit", onSubmit, true);
  }, []);

  // Show loading indicator or error
  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div>
      <SEO
        title="Grad4Grad - Your Gateway to International Education"
        description="Discover top universities worldwide, get personalized study abroad guidance, scholarship opportunities, and expert advice for international students. Start your global education journey today!"
        keywords="study abroad, international universities, scholarships, university admissions, study in USA, study in Canada, international students, graduate school, masters degree, PhD programs"
        url="https://grad4grad.com"
        type="website"
      />
      {/* Hero Section */}
      <div
        className="hero-section"
        style={{
          background: `linear-gradient(to right, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.4)), url(${heroBackground})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundColor: "#2b3445",
        }}
      >
        <div className="hero-content">
          <h1>Start Your Study Abroad Journey Today</h1>
          <p>
            Find your dream university and get personalized guidance on
            admissions process, scholarships, and visa applications.
          </p>
          <div className="hero-stats">
            <div className="hero-stat-item">
              <span className="hero-stat-number">15k+</span>
              <span className="hero-stat-text">Successful Admits</span>
            </div>
            <div className="hero-stat-item">
              <span className="hero-stat-number">$15M+</span>
              <span className="hero-stat-text">Scholarships Achieved</span>
            </div>
            <div className="hero-stat-item">
              <span className="hero-stat-number">98%</span>
              <span className="hero-stat-text">Visa Success Rate</span>
            </div>
          </div>
          <div className="button-container">
            <a href="#university-section" className="cta-button">
              Find Your University
            </a>
            <button
              onClick={() => navigate("/rate-my-chances")}
              className="cta-button secondary"
            >
              Rate My Chances
            </button>
          </div>
        </div>
      </div>
      {/* Featured Countries Section */}
      <div className="featured-countries-section">
        <h2>Popular Study Destinations</h2>
        <div className="countries-grid">
          <div
            className="country-card usa"
            style={{
              backgroundImage: `url(${usaCountry})`,
              backgroundColor: "#1a365d",
            }}
          >
            <h3>United States</h3>
            <p>
              Home to world-renowned universities like Harvard, MIT, and
              Stanford
            </p>
          </div>
          <div
            className="country-card canada"
            style={{
              backgroundImage: `url(${canadaCountry})`,
              backgroundColor: "#2d3748",
            }}
          >
            <h3>Canada</h3>
            <p>
              Known for quality education, affordability and post-graduation
              work permits
            </p>
          </div>
          <div
            className="country-card uk"
            style={{
              backgroundImage: `url(${ukCountry})`,
              backgroundColor: "#2c5282",
            }}
          >
            <h3>United Kingdom</h3>
            <p>
              Prestigious education system with universities like Oxford and
              Cambridge
            </p>
          </div>
          <div
            className="country-card australia"
            style={{
              backgroundImage: `url(${australiaCountry})`,
              backgroundColor: "#2a4365",
            }}
          >
            <h3>Australia</h3>
            <p>
              High-quality education with a great lifestyle and beautiful
              surroundings
            </p>
          </div>
        </div>
      </div>
      {/* University Section */}
      <div id="university-section" className="university-section">
        <h2>Explore Universities</h2>
        {!isLoggedIn && (
          <div className="upgrade-prompt">
            <div className="upgrade-prompt-content">
              <h3>🔒 Basic View</h3>
              <p>
                You're viewing basic university information. Login to access
                detailed data including tuition fees, acceptance rates, and
                student information.
              </p>
              <button onClick={() => navigate("/login")} className="cta-button">
                Login for Full Access
              </button>
            </div>
          </div>
        )}
        {/* Filters and Search UI (copy from App.js) */}
        <div className="filters">
          {/* Search Bar with Suggestions */}
          <form
            className="search-container"
            onSubmit={handleSearchSubmit}
            autoComplete="off"
          >
            <div className="search-input-wrapper">
              <input
                type="text"
                className="search-bar"
                placeholder="Search universities by name..."
                value={searchTerm}
                onChange={handleSearchChange}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                aria-label="Search universities"
                autoComplete="off"
              />
              {searchTerm && (
                <button
                  type="button"
                  className="search-clear-button"
                  onClick={() => setSearchTerm("")}
                  tabIndex={-1}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
              {searchLoading && (
                <span className="search-loading-indicator">Loading...</span>
              )}
            </div>
            {/* Suggestions Dropdown */}
            {showSuggestions && (
              <ul
                className={`search-suggestions${
                  suggestionsLoading ? " loading" : ""
                }`}
              >
                {suggestionsLoading && (
                  <li className="loading-item">Loading suggestions...</li>
                )}
                {!suggestionsLoading &&
                  universitySuggestions.length > 0 &&
                  universitySuggestions.map((suggestion, idx) => {
                    const hasCity =
                      suggestion.city && suggestion.city.trim() !== "";
                    const hasState =
                      suggestion.state && suggestion.state.trim() !== "";
                    const hasCountry =
                      suggestion.country && suggestion.country.trim() !== "";
                    return (
                      <li
                        key={suggestion.slug || suggestion.name || idx}
                        className={
                          idx === selectedSuggestionIndex ? "selected" : ""
                        }
                        onMouseDown={() => handleSuggestionSelect(suggestion)}
                      >
                        <span className="suggestion-name">
                          {suggestion.name}
                        </span>
                        <span className="suggestion-location">
                          {hasCity && suggestion.city}
                          {hasState &&
                            `${hasCity ? ", " : ""}${suggestion.state}`}
                          {hasCountry &&
                            (hasCity || hasState
                              ? `, ${suggestion.country}`
                              : suggestion.country)}
                        </span>
                      </li>
                    );
                  })}
                {!suggestionsLoading && universitySuggestions.length === 0 && (
                  <li className="no-results">No suggestions found</li>
                )}
              </ul>
            )}
          </form>

          {/* Country Filter */}
          <select
            className="country-filter"
            value={selectedCountry}
            onChange={handleCountryChange}
            aria-label="Filter by country"
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
          >
            <option value="All">All Countries</option>
            <option value="USA">USA</option>
            <option value="Canada">Canada</option>
          </select>

          {/* Tuition Fee Filter (only for logged in users) */}
          {isLoggedIn && (
            <select
              className="tuition-filter"
              value={tuitionFeeRange}
              onChange={handleTuitionFeeChange}
              aria-label="Filter by tuition fee"
              onKeyDown={(e) => {
                if (e.key === "Enter") e.preventDefault();
              }}
            >
              <option value="All">All Tuition Fees</option>
              <option value="25000">Up to $25,000</option>
              <option value="35000">$25,001 - $35,000</option>
              <option value="45000">$35,001 - $45,000</option>
              <option value="55000">$45,001 - $55,000</option>
              <option value="65000">$55,001 - $65,000</option>
              <option value="75000">$65,001 - $75,000</option>
              <option value="85000">$75,001 - $85,000</option>
              <option value="85000+">Above $85,000</option>
            </select>
          )}
        </div>
        {/* Filter loading indicator */}
        {searchLoading && (
          <span className="filter-loading-indicator">Filtering...</span>
        )}
        <UniversityList universities={universities} isLoggedIn={isLoggedIn} />
        {/* Load More Button */}
        {hasMore && (
          <div className="load-more-container">
            <button
              className="load-more-button"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? "Loading..." : "Load More Universities"}
            </button>
          </div>
        )}
      </div>
      <BackToTop />
    </div>
  );
};

export default HomePage;
